import apiService from '@/services/ApiService';
import { Logger } from '@/other/Logger';
import { ConnectorConfig } from '@/models/ConnectorConfig';

const log = new Logger('ConnectorSyncPollingService');

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_MAX_CONSECUTIVE_ERRORS = 5;

type SyncStatus = 'in_progress' | 'done' | 'error' | 'idle';

interface SyncStatusResponse {
  status: SyncStatus;
  connector: unknown;
  workspaceOrder?: unknown[];
  error?: string;
  jobId?: string | null;
}

export interface ConnectorSyncResult {
  connector: ConnectorConfig;
  workspaceOrder: unknown[];
}

interface PollOptions {
  pollIntervalMs?: number;
  /** Bail after this many CONSECUTIVE network/API errors. A successful tick resets the counter. */
  maxConsecutiveErrors?: number;
  /** Epoch ms snapshot of the connector's lastSyncAt before triggerSync was called. */
  since?: number;
}

class ConnectorSyncPollingService {
  private activePolls: Map<string, ReturnType<typeof setInterval>> = new Map();
  private cancelledPolls: Set<string> = new Set();

  poll(connectorId: string, options: PollOptions = {}): Promise<ConnectorSyncResult> {
    const pollInterval = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const maxConsecutiveErrors = options.maxConsecutiveErrors ?? DEFAULT_MAX_CONSECUTIVE_ERRORS;

    this.cancel(connectorId);
    this.cancelledPolls.delete(connectorId);

    return new Promise<ConnectorSyncResult>((resolve, reject) => {
      let consecutiveErrors = 0;

      const tick = async () => {
        if (this.cancelledPolls.has(connectorId)) {
          this.cancelledPolls.delete(connectorId);
          this.stop(connectorId);
          reject(new Error('Sync polling cancelled'));
          return;
        }

        try {
          const response = await apiService.connector.getSyncStatus(connectorId, options.since);
          const data = response.data as SyncStatusResponse;
          consecutiveErrors = 0;

          if (data.status === 'done') {
            this.stop(connectorId);
            resolve({
              connector: ConnectorConfig.parse(data.connector),
              workspaceOrder: data.workspaceOrder ?? [],
            });
            return;
          }

          if (data.status === 'error') {
            this.stop(connectorId);
            reject(new Error(data.error || 'Sync failed'));
            return;
          }

          // 'in_progress' or 'idle' — keep polling. Long syncs (>10min) are intentionally
          // not capped here; the global ConnectorSyncMonitor owns the lifecycle and the
          // user can navigate freely while we wait.
        } catch (err: any) {
          consecutiveErrors += 1;
          log.info(`Sync status poll failed (consecutive error ${consecutiveErrors}): ${err?.message || err}`);
          if (consecutiveErrors >= maxConsecutiveErrors) {
            this.stop(connectorId);
            reject(new Error(err?.response?.data?.error || 'Failed to check sync status'));
          }
        }
      };

      tick();
      const intervalId = setInterval(tick, pollInterval);
      this.activePolls.set(connectorId, intervalId);
    });
  }

  cancel(connectorId: string): void {
    if (this.activePolls.has(connectorId)) {
      this.cancelledPolls.add(connectorId);
      this.stop(connectorId);
    }
  }

  private stop(connectorId: string): void {
    const intervalId = this.activePolls.get(connectorId);
    if (intervalId) {
      clearInterval(intervalId);
      this.activePolls.delete(connectorId);
    }
  }
}

const connectorSyncPollingService = new ConnectorSyncPollingService();
export default connectorSyncPollingService;
